<template>
    <Dialog :visible="open" modal dismissableMask :closable="false" :draggable="false"
        contentClass="!p-0 overflow-hidden rounded-lg" :style="{ width: '65rem', maxWidth: '95vw' }"
        :pt="{ header: { class: '!p-0' } }" @update:visible="(v) => !v && $emit('close')">
        <template #header>
            <div
                class="flex items-center justify-between w-full p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-gray-900 rounded-t-lg">
                <div class="flex items-center gap-2">
                    <div
                        class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 dark:bg-blue-700 flex items-center justify-center">
                        <i class="fa-regular fa-calendar text-white text-lg"></i>
                    </div>
                    <h4 class="text-xl font-bold">{{ reservation?.title || '-' }}</h4>
                </div>
                <Button icon="fa-solid fa-xmark" rounded text :aria-label="t('common.close')" class="flex-shrink-0"
                    @click="$emit('close')" />
            </div>
        </template>

        <!-- BODY -->
        <div v-if="reservation" class="px-4 pb-4 max-h-[70vh] overflow-y-auto mt-2">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

                <!-- ===== LEFT PANEL: Info + Client ===== -->
                <div class="space-y-4">

                    <!-- Reservation Info -->
                    <div>
                        <span class="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                            {{ t('calendar.detail.info') }}
                        </span>
                        <div
                            class="mt-2 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 p-4 space-y-4">
                            <!-- Status badge -->
                            <Tag :value="getStatusLabel(reservation.status)"
                                :severity="getStatusSeverity(reservation.status)" />

                            <!-- Data grid -->
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                <div>
                                    <p class="text-xs text-surface-400 mb-0.5">{{
                                        t('calendar.new.reservation.attendees') }}</p>
                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                        reservation.attendees || 0 }}</p>
                                </div>
                                <div>
                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.detail.createdBy') }}</p>
                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">
                                        {{ reservation.createdBy ? `${reservation.createdBy.firstName}
                                        ${reservation.createdBy.lastName}` : t('calendar.detail.notAvailable') }}
                                    </p>
                                </div>
                                <div v-if="reservation.description" class="sm:col-span-2">
                                    <p class="text-xs text-surface-400 mb-0.5">{{
                                        t('calendar.new.reservation.description') }}
                                    </p>
                                    <p
                                        class="text-sm font-medium text-surface-700 dark:text-surface-200 whitespace-pre-wrap">
                                        {{
                                            reservation.description }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Client Details -->
                    <div v-if="reservation.visitors && reservation.visitors.length > 0"
                        class="border-t border-surface-200 dark:border-surface-700 pt-4">
                        <span class="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                            {{ t('calendar.detail.client') }}
                        </span>
                        <div
                            class="mt-2 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 p-4 space-y-4">
                            <!-- Header: type badge + name -->
                            <div class="space-y-2">
                                <span
                                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300">
                                    <i class="fa-solid fa-tag text-[10px]"></i>
                                    {{ getClientTypeLabel(reservation.visitors[0].type) }}
                                </span>
                                <div>
                                    <p class="font-semibold text-base leading-tight">{{
                                        reservation.visitors[0].firstName }} {{
                                            reservation.visitors[0].lastName }}</p>
                                    <p v-if="reservation.visitors[0].erpCode" class="text-xs text-surface-400 mt-0.5">{{
                                        reservation.visitors[0].erpCode }}</p>
                                </div>
                            </div>

                            <!-- Data grid -->
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                <!-- Email (all types) -->
                                <div>
                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.detail.email') }}</p>
                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200 truncate">{{
                                        reservation.visitors[0].email || '-' }}</p>
                                </div>

                                <!-- NonProfessional extras -->
                                <template v-if="reservation.visitors[0].type === VisitorType.NonProfessional">
                                    <div v-if="reservation.visitors[0].role">
                                        <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.role') }}
                                        </p>
                                        <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                            reservation.visitors[0].role }}</p>
                                    </div>
                                    <div v-if="reservation.visitors[0].activitySector">
                                        <p class="text-xs text-surface-400 mb-0.5">{{
                                            t('calendar.new.visitors.activitySector') }}</p>
                                        <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                            reservation.visitors[0].activitySector }}</p>
                                    </div>
                                </template>

                                <!-- NewProfessional extras -->
                                <template v-if="reservation.visitors[0].type === VisitorType.NewProfessional">
                                    <div v-if="reservation.visitors[0].country">
                                        <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.detail.country') }}
                                        </p>
                                        <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                            reservation.visitors[0].country.name }}</p>
                                    </div>
                                    <div v-if="reservation.visitors[0].commercialSegment">
                                        <p class="text-xs text-surface-400 mb-0.5">{{
                                            t('calendar.detail.commercialSegment') }}</p>
                                        <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                            reservation.visitors[0].commercialSegment.name }}</p>
                                    </div>
                                    <div v-if="reservation.visitors[0].businessName">
                                        <p class="text-xs text-surface-400 mb-0.5">{{
                                            t('calendar.new.visitors.businessName') }}</p>
                                        <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                            reservation.visitors[0].businessName }}</p>
                                    </div>
                                    <div
                                        v-if="reservation.visitors[0].functionaryName || reservation.visitors[0].functionaryId">
                                        <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.detail.functionary')
                                            }}</p>
                                        <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                            reservation.visitors[0].functionaryName ||
                                            reservation.visitors[0].functionaryId }}</p>
                                    </div>
                                    <div v-if="reservation.visitors[0].agentName || reservation.visitors[0].agentId">
                                        <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.detail.agent') }}</p>
                                        <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{
                                            reservation.visitors[0].agentName || reservation.visitors[0].agentId }}</p>
                                    </div>
                                </template>

                                <!-- ErpProfessional: purchase group -->
                                <div
                                    v-if="reservation.visitors[0].type === VisitorType.ErpProfessional && reservation.visitors[0].representsPurchaseGroup != null">
                                    <p class="text-xs text-surface-400 mb-0.5">{{
                                        t('calendar.detail.representsPurchaseGroup')
                                        }}</p>
                                    <p class="text-sm font-medium">
                                        <i v-if="reservation.visitors[0].representsPurchaseGroup"
                                            class="fa-solid fa-check text-green-600 dark:text-green-400"></i>
                                        <i v-else class="fa-solid fa-xmark text-red-500 dark:text-red-400"></i>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <!-- ===== END LEFT PANEL ===== -->

                <!-- ===== RIGHT PANEL: Resources Timeline ===== -->
                <div>
                    <div class="flex items-center gap-2 mb-3">
                        <span class="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                            {{ t('calendar.detail.resources') }}
                        </span>
                        <Tag :value="reservation.slots.length.toString()" severity="info" class="!text-xs" />
                    </div>

                    <div class="relative">
                        <!-- Full-height connecting line -->
                        <div
                            class="absolute inset-y-0 left-1/2 -translate-x-px w-0.5 bg-surface-200 dark:bg-surface-700">
                        </div>

                        <div v-for="(slot, index) in sortedSlots" :key="index"
                            class="relative grid items-start gap-x-4 py-3" style="grid-template-columns: 1fr auto 1fr">
                            <!-- Left: time -->
                            <div class="flex flex-col items-end gap-0.5">
                                <span class="text-sm font-medium tabular-nums text-surface-700 dark:text-surface-200">
                                    {{ formatResourceRange(slot.startMs, slot.endMs, slotTimezoneId(slot.resourceId)) }}
                                </span>
                                <!-- The operator's OWN clock, demoted beneath the space's and rendered per slot only
                                     when that slot's space sits on a different offset (D-06: omitted entirely
                                     otherwise, never an em dash). Unlike the preview card this is a narrow
                                     right-aligned grid track with no room for a visible label, so the label moves into
                                     a tooltip and the icon therefore keeps an accessible name of its own. -->
                                <span v-if="showBrowserLocalTime(slot.startMs, slot.resourceId)"
                                    class="text-xs text-surface-400 tabular-nums"
                                    v-tooltip.top="t('calendar.timezone.browserLocalTime')">
                                    <i class="fa-solid fa-globe" role="img"
                                        :aria-label="t('calendar.timezone.differsAria')"></i>
                                    {{ formatRange(slot.startMs, slot.endMs) }}
                                </span>
                                <span class="text-xs text-surface-400">{{ calculateDuration(slot.startMs, slot.endMs)
                                    }}</span>
                            </div>
                            <!-- Center: colored dot -->
                            <div class="relative z-10 mt-1 w-4 h-4 rounded-full ring-2 ring-white dark:ring-surface-900 shrink-0"
                                :style="{ backgroundColor: '#' + getSlotResourceColor(slot.resourceId) }"></div>
                            <!-- Right: resource + custom fields -->
                            <div class="min-w-0">
                                <p class="font-semibold text-sm truncate mb-1">{{
                                    resourcesStore.getResourceDisplayNameById(slot.resourceId) }}</p>
                                <p v-if="getSlotResourceType(slot.resourceId)"
                                    class="text-xs text-surface-400 truncate">{{
                                        getSlotResourceType(slot.resourceId) }}</p>

                                <!-- Custom Fields -->
                                <div v-if="slot.customFields && slot.customFields.length > 0" class="mt-2 space-y-1">
                                    <div v-for="customField in slot.customFields" :key="customField.customFieldId">
                                        <div v-if="typeof customField.value === 'boolean'"
                                            class="flex items-center gap-1.5 text-xs">
                                            <i :class="customField.value ? 'fa-regular fa-square-check text-green-600 dark:text-green-400' : 'fa-regular fa-square text-surface-400'"
                                                class="text-sm"></i>
                                            <span v-tooltip="getCustomFieldDescription(customField.customFieldId)"
                                                class="font-medium text-surface-600 dark:text-surface-300">
                                                {{ getCustomFieldTitle(customField.customFieldId) }}
                                            </span>
                                        </div>
                                        <p v-if="typeof customField.value === 'boolean' && customField.notes"
                                            class="text-xs text-surface-400 ml-5">{{ customField.notes }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- ===== END RIGHT PANEL ===== -->

            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="p-4 md:p-8 text-center">
            <div
                class="w-16 h-16 md:w-20 md:h-20 rounded-full bg-surface-100 dark:bg-surface-800 mx-auto flex items-center justify-center mb-3">
                <i class="fa-solid fa-inbox text-5xl text-surface-400"></i>
            </div>
            <p class="text-lg md:text-xl font-medium">{{ t('calendar.detail.notFound') }}</p>
            <p class="mt-1 text-surface-500">{{ t('calendar.detail.notFoundDetails') }}</p>
        </div>

        <!-- Past unconfirmed visit banner (D-04) -->
        <div v-if="showUnconfirmedBanner" class="px-3 pt-2">
            <div
                class="flex items-center gap-2 p-3 rounded-lg border border-red-500 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border-red-400">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span class="text-sm font-medium">{{ t('calendar.detail.unconfirmedBanner') }}</span>
            </div>
        </div>

        <!-- FOOTER -->
        <div
            class="flex flex-col md:flex-row justify-end p-3 border-t border-surface-200 bg-surface-50 dark:bg-surface-800 gap-2 md:gap-0">
            <Button :label="t('cancelReservation.title')" icon="fa-regular fa-calendar-xmark" severity="danger" outlined
                :loading="isCanceling" @click="handleCancel" class="md:mr-auto" />
            <Button
                v-if="reservation && reservation.type === ReservationType.External && reservation.status === ReservationStatus.Planned && reservation.slots.every((s) => s.endMs < Date.now())"
                :label="t('confirmReservation.title')" icon="fa-solid fa-check" severity="success" outlined
                @click="handleConfirm" class="md:mr-2" />
            <Button
                v-if="reservation && (reservation.status === ReservationStatus.Planned || (reservation.status === ReservationStatus.Completed && reservation.slots.length > 0 && reservation.slots.every((s) => s.startMs > Date.now())))"
                :label="t('common.edit')" icon="fa-regular fa-pen-to-square" severity="primary" @click="handleEdit" />
        </div>
    </Dialog>

    <!-- Cancel confirm Dialog -->
    <Dialog v-model:visible="showCancelConfirm" modal :draggable="false" :closable="false"
        :style="{ width: '28rem', maxWidth: '90vw' }" contentClass="!p-0 overflow-hidden rounded-lg"
        :pt="{ header: { class: '!p-0' } }" @update:visible="(v) => !v && (showCancelConfirm = false)">
        <template #header>
            <div class="flex items-center justify-between w-full p-3 bg-gray-50 rounded-t-lg">
                <div class="flex items-center gap-3">
                    <div
                        class="w-8 h-8 md:w-10 md:h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                        <i class="fa-solid fa-triangle-exclamation text-red-600 dark:text-red-400 text-lg"></i>
                    </div>
                    <h4 class="text-lg md:text-xl font-semibold">{{ t('cancelReservation.confirm') }}</h4>
                </div>
                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    class="flex-shrink-0" @click="showCancelConfirm = false" />
            </div>
        </template>

        <div class="p-2">
            <p>
                {{ t('cancelReservation.message') }}
            </p>
        </div>

        <template #footer :class="p - 0">
            <div class="flex w-full justify-end p-3 border-t bg-gray-50">
                <Button :label="t('cancelReservation.title')" icon="fa-regular fa-calendar-xmark" severity="danger"
                    class="!rounded-lg" :loading="isCanceling" @click="confirmCancel" />
            </div>
        </template>
    </Dialog>

    <!-- Confirm reservation dialog -->
    <Dialog v-if="reservation && reservation.status === ReservationStatus.Planned" v-model:visible="showConfirmDialog"
        modal :draggable="false" :closable="false" :style="{ width: '28rem', maxWidth: '90vw' }"
        contentClass="!p-0 overflow-hidden rounded-lg" :pt="{ header: { class: '!p-0' } }"
        @update:visible="(v) => !v && (showConfirmDialog = false)">
        <template #header>
            <div class="flex items-center justify-between w-full p-3 bg-green-50 rounded-t-lg">
                <div class="flex items-center gap-3">
                    <div class="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <i class="fa-solid fa-check text-green-600 text-lg"></i>
                    </div>
                    <h4 class="text-lg md:text-xl font-semibold">{{ t('confirmReservation.title') }}</h4>
                </div>
                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    class="flex-shrink-0" @click="showConfirmDialog = false" />
            </div>
        </template>

        <div class="p-2">
            <p>
                {{ t('confirmReservation.message') }}
            </p>
        </div>

        <template #footer :class="p - 0">
            <div class="flex w-full justify-end p-3 border-t bg-gray-50">
                <Button :label="t('confirmReservation.title')" icon="fa-solid fa-check" severity="success"
                    class="!rounded-lg" @click="confirmReservation" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { isPastUnconfirmed } from '@/lib/services/reservation';
import { Reservation, ReservationStatus, ReservationType } from '@/lib/types/reservation';
import { VisitorType } from '@/lib/types/visitor';
import { getErrorMessage, getReservationStatusLabel, getVisitorTypeLabel } from '@/plugins/i18n/enums';
import { useCustomFieldsStore } from '@/store/customField';
import { useReservationsStore } from '@/store/reservation';
import { useResourcesStore } from '@/store/resource';
import { isForeignTimezoneAt, msToResourceLocalCalendarDate, msToResourceLocalTimeString } from '@/utils/date';
import { useToast } from 'primevue/usetoast';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t, locale } = useI18n();

const router = useRouter();
const resourcesStore = useResourcesStore();
const reservationsStore = useReservationsStore();
const customFieldsStore = useCustomFieldsStore();
const toast = useToast();

const props = defineProps<{
    open: boolean;
    reservation: Reservation | null;
    highlightResourceId: string | null;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'edit'): void;
    (e: 'cancel'): void;
    (e: 'canceled'): void;
    (e: 'confirmed'): void;
}>();

const sortedSlots = computed(() =>
    props.reservation ? [...props.reservation.slots].sort((a, b) => a.startMs - b.startMs) : []
);

// D-04: same shared predicate as the calendar badge — day-based in the resource timezone, so the banner can
// appear the next resource-local day while the confirm button's endMs gate may already be open the same evening.
const showUnconfirmedBanner = computed(() => (props.reservation ? isPastUnconfirmed(props.reservation, resourcesStore.getResourceById) : false));

function getSlotResourceColor(resourceId: string): string {
    return resourcesStore.resources.find(r => r.id === resourceId)?.color || '8B5CF6';
}

function getSlotResourceType(resourceId: string): string {
    return resourcesStore.resources.find(r => r.id === resourceId)?.type?.name || '';
}

/**
 * The zone seam, resolved PER SLOT: a reservation's slots may sit on spaces on different clocks, so there is no one
 * zone for the modal. Same lookup idiom the wizard uses. Null when the space or its zone is unknown, which sends every
 * consumer down the D-09 browser-local fallback.
 */
function slotTimezoneId(resourceId: string): string | null {
    return resourcesStore.getResourceById(resourceId)?.timezone?.id ?? null;
}

const showConfirmDialog = ref(false);

const showCancelConfirm = ref(false);
const isCanceling = ref(false);

function handleEdit() {
    if (!props.reservation?.id) return;

    // Navigate to edit page with reservation ID
    router.push({
        name: 'edit',
        params: { id: props.reservation.id }
    });
}

function handleConfirm() {
    showConfirmDialog.value = true;
}

function handleCancel() {
    showCancelConfirm.value = true;
}

async function confirmReservation() {
    if (!props.reservation?.id) return;

    try {
        const error = await reservationsStore.confirmOne(props.reservation);
        if (error) {
            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: getErrorMessage(error, t),
                life: 5000
            });
            return;
        }

        toast.add({
            severity: 'success',
            summary: t('confirmReservation.success'),
            detail: t('confirmReservation.success'),
            life: 3000
        });

        showConfirmDialog.value = false;
        emit('confirmed');
        emit('close');
    } catch (error) {
        console.error('Error confirming reservation:', error);

        toast.add({
            severity: 'error',
            summary: t('error.title'),
            detail: t('confirmReservation.error'),
            life: 5000
        });
    }
}

async function confirmCancel() {
    if (!props.reservation?.id) return;

    isCanceling.value = true;

    try {
        const error = await reservationsStore.cancelOne(props.reservation.id);

        if (error) {
            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: getErrorMessage(error, t),
                life: 5000
            });
            return;
        } else {
            toast.add({
                severity: 'success',
                summary: t('cancelReservation.success'),
                detail: t('cancelReservation.success'),
                life: 3000
            });
        }

        showCancelConfirm.value = false;
        emit('canceled');
        emit('close');
    } catch (error) {
        console.error('Error canceling reservation:', error);

        toast.add({
            severity: 'error',
            summary: t('error.title'),
            detail: t('cancelReservation.error'),
            life: 5000
        });
    } finally {
        isCanceling.value = false;
    }
}

function getStatusLabel(status: ReservationStatus): string {
    const labels: Record<ReservationStatus, string> = {
        [ReservationStatus.Planned]: t(getReservationStatusLabel(ReservationStatus.Planned)),
        [ReservationStatus.Canceled]: t(getReservationStatusLabel(ReservationStatus.Canceled)),
        [ReservationStatus.Completed]: t(getReservationStatusLabel(ReservationStatus.Completed))
    };
    return labels[status] || t('calendar.detail.unknown');
}

function getStatusSeverity(status: ReservationStatus): 'success' | 'info' | 'warning' | 'danger' {
    const severities: Record<ReservationStatus, 'success' | 'info' | 'warning' | 'danger'> = {
        [ReservationStatus.Planned]: 'warning',
        [ReservationStatus.Canceled]: 'danger',
        [ReservationStatus.Completed]: 'info'
    };
    return severities[status] || 'info';
}

function getClientTypeLabel(type: VisitorType): string {
    const labels: Record<VisitorType, string> = {
        [VisitorType.NonProfessional]: t(getVisitorTypeLabel(VisitorType.NonProfessional)),
        [VisitorType.NewProfessional]: t(getVisitorTypeLabel(VisitorType.NewProfessional)),
        [VisitorType.ErpProfessional]: t(getVisitorTypeLabel(VisitorType.ErpProfessional))
    };
    return labels[type] || t('calendar.detail.unknown');
}

/**
 * THE SECONDARY renderer: the operator's own browser clock. Behaviourally unchanged — it must keep producing today's
 * exact string, because it is the reference the same-zone case is compared against. The only edit is that it no longer
 * instantiates the i18n composable from inside a function body, which was a defect; it reads the setup-scope locale.
 */
function formatRange(start: number, end: number) {
    const s = new Date(start);
    const e = new Date(end);

    const startDate = s.toLocaleDateString(locale.value, { day: '2-digit', month: 'short' });
    const startTime = s.toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' });
    const endTime = e.toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' });

    return t('calendar.detail.rangeFormat', { date: startDate, start: startTime, end: endTime });
}

/**
 * THE PRIMARY renderer: the same range on the SPACE's clock. Same i18n key, same interpolation shape as the browser
 * renderer above.
 *
 * Asymmetry on purpose: the primary's times are fixed `HH:mm` from the shared helper while the secondary keeps its
 * locale-formatted times. Both render 24-hour in it-IT and en-GB, the only two locales on disk, so the two lines agree
 * in shape — and the asymmetry is what keeps the secondary byte-identical to today's rendering.
 *
 * `slot.startMs` is a TRUE instant off the store, never FullCalendar's display instant, so this conversion is still
 * required and is not the grid's shift read back.
 */
function formatResourceRange(start: number, end: number, timeZoneId: string | null) {
    const startDate = (msToResourceLocalCalendarDate(timeZoneId, start) ?? new Date(start)).toLocaleDateString(locale.value, { day: '2-digit', month: 'short' });
    const startTime = msToResourceLocalTimeString(timeZoneId, start);
    const endTime = msToResourceLocalTimeString(timeZoneId, end);

    return t('calendar.detail.rangeFormat', { date: startDate, start: startTime, end: endTime });
}

/**
 * Whether this slot's space sits on a different UTC offset than the operator's browser at the slot's own start
 * instant — the same single predicate the hover preview card uses (IC-1). Per slot, so a multi-space reservation
 * shows the secondary line only on the slots that need it.
 */
function showBrowserLocalTime(startMs: number, resourceId: string): boolean {
    return isForeignTimezoneAt(slotTimezoneId(resourceId), startMs);
}

function calculateDuration(start: number, end: number) {
    const diff = end - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
        return `${hours}h`;
    } else {
        return `${minutes}m`;
    }
}

function getCustomFieldTitle(fieldId: string): string {
    return customFieldsStore.customFields.find((cf) => cf.id === fieldId)?.title || fieldId;
}

function getCustomFieldDescription(fieldId: string): string | null {
    return customFieldsStore.customFields.find((cf) => cf.id === fieldId)?.description || null;
}
</script>
